import { useEffect } from "react";
import { siteConfig } from "@/lib/site-config";

interface SeoProps {
  title: string;
  description: string;
  path: string;
  ogType?: "website" | "article";
  ogImage?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  /** Set for pages that should stay out of the index (none today). */
  noIndex?: boolean;
}

/** Marks the tags this component owns, so it only ever replaces its own. */
const OWNED = "data-seo";

function upsertMeta(selector: string, attrs: Record<string, string>) {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(OWNED, "");
    document.head.appendChild(el);
  }
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
}

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    el.setAttribute(OWNED, "");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/**
 * Per-route document head.
 *
 * This replaces react-helmet-async, which silently rendered nothing on this
 * project: no title update on navigation, and no canonical, robots or
 * per-page Open Graph tags in the document at all. Netlify's seo-injection
 * edge function still writes the same tags server-side for crawlers; this
 * keeps the client in step after a client-side route change, and is what a
 * Lighthouse run against the built site sees.
 */
const Seo = ({
  title,
  description,
  path,
  ogType = "website",
  ogImage,
  jsonLd,
  noIndex = false,
}: SeoProps) => {
  const url = `${siteConfig.url}${path}`;
  const image = ogImage
    ? ogImage.startsWith("http")
      ? ogImage
      : `${siteConfig.url}${ogImage}`
    : `${siteConfig.url}${siteConfig.ogImage}`;
  const imageAlt = `${siteConfig.name} — ${siteConfig.tagline}`;
  const serialisedLd = jsonLd
    ? JSON.stringify(Array.isArray(jsonLd) ? jsonLd : [jsonLd])
    : null;

  useEffect(() => {
    document.title = title;
    document.documentElement.lang = "en";

    upsertMeta('meta[name="description"]', { name: "description", content: description });
    upsertMeta('meta[name="robots"]', {
      name: "robots",
      content: noIndex ? "noindex, nofollow" : "index, follow, max-image-preview:large",
    });
    upsertLink("canonical", url);

    upsertMeta('meta[property="og:type"]', { property: "og:type", content: ogType });
    upsertMeta('meta[property="og:title"]', { property: "og:title", content: title });
    upsertMeta('meta[property="og:description"]', { property: "og:description", content: description });
    upsertMeta('meta[property="og:url"]', { property: "og:url", content: url });
    upsertMeta('meta[property="og:image"]', { property: "og:image", content: image });
    upsertMeta('meta[property="og:image:alt"]', { property: "og:image:alt", content: imageAlt });
    upsertMeta('meta[property="og:site_name"]', { property: "og:site_name", content: siteConfig.name });
    upsertMeta('meta[property="og:locale"]', { property: "og:locale", content: "en_US" });

    upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: title });
    upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: description });
    upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: image });
    upsertMeta('meta[name="twitter:image:alt"]', { name: "twitter:image:alt", content: imageAlt });
  }, [title, description, url, ogType, image, imageAlt, noIndex]);

  useEffect(() => {
    if (!serialisedLd) return;
    // Structured data is per-route, so this block is created and torn down
    // with the page rather than merged into whatever was there before.
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute(OWNED, "");
    script.textContent = serialisedLd;
    document.head.appendChild(script);
    return () => script.remove();
  }, [serialisedLd]);

  return null;
};

export default Seo;
